import locationService from '../../../services/locationService';
import filterService from '../../../services/filterService';

import {
    setLocationLoading,
    setLocationError,
    setLocationList,
    setLocationCurrent,
    setLocationPagination,
    setLocationSort,
    setLocationFilters,
    setLocationDefaultFilter,
    setLocationSettings,
    clearLocationCurrent,
    clearLocationError,
    setLocationFilterLoading,
    changeLocationFilterCondition,
    addLocationFilterCondition,
    updateLocationFilterConditionRule,
    updateLocationFilterConditionValue,
    removeLocationFilterCondition,
    setLocationSelected,
    resetLocationFiltersAndPagination
} from './locationSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().location.isSettingsInitialized) {
            dispatch(setLocationLoading(true));
            const response = await locationService.getSettings();

            // Set the settings
            dispatch(setLocationSettings(response));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setLocationDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setLocationError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setLocationLoading(false));
    }
};

export const fetchLocations = (params = {}, filter = {}, shouldUseFilter) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().location;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : {};

        dispatch(setLocationLoading(true));
        const response = await locationService.getList(finalQueryParams, finalActiveFilter);
        dispatch(setLocationList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchLocations:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setLocationError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setLocationLoading(false));
    }
};

export const fetchLocation = (id) => async (dispatch) => {
    try {
        dispatch(setLocationLoading(true));
        const response = await locationService.getOne(id);
        dispatch(setLocationCurrent(response.data));
    } catch (error) {
        dispatch(setLocationError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setLocationLoading(false));
    }
};

export const createLocation = (data) => async (dispatch) => {
    try {
        const response = await locationService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateLocation = (id, data) => async (dispatch) => {
    try {
        const response = await locationService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteLocation = (id) => async (dispatch) => {
    try {
        dispatch(setLocationLoading(true));
        const response = await locationService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setLocationError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setLocationLoading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setLocationPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setLocationSort(sort));
};

export const searchLocations = (query) => (dispatch, getState) => {
    const currentFilters = getState().location.filters;
    dispatch(setLocationFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setLocationDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeLocationFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addLocationFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateLocationFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateLocationFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeLocationFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setLocationFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().location.settings;
            dispatch(setLocationSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setLocationFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setLocationFilterLoading(false));
        dispatch(setLocationError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setLocationSelected(selectedRows));
    };
};

export const clearCurrentLocation = () => {
    return (dispatch) => {
        dispatch(clearLocationCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetLocationFiltersAndPagination());
};